import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';

import { useUiStore, type ToastKind } from '@/store/ui.store';
import { cn } from '@/lib/utils';

const KIND_STYLE: Record<ToastKind, string> = {
  info: 'border-border bg-fg/90 text-white',
  success: 'border-emerald-200 bg-emerald-600 text-white',
  warning: 'border-amber-200 bg-amber-600 text-white',
  error: 'border-red-200 bg-red-600 text-white',
};

function KindIcon({ kind }: { kind: ToastKind }) {
  const cls = 'shrink-0';
  switch (kind) {
    case 'success':
      return <CheckCircle2 size={14} className={cls} />;
    case 'warning':
      return <AlertTriangle size={14} className={cls} />;
    case 'error':
      return <XCircle size={14} className={cls} />;
    default:
      return <Info size={14} className={cls} />;
  }
}

/**
 * Bottom-center toast stack. Mount once near the root (`AppShell`). Push via
 * `useUiStore.getState().pushToast(...)` or the `pushToast()` helper from
 * non-React callers (drop handlers, store actions).
 */
export function ToastContainer() {
  const toasts = useUiStore((s) => s.toasts);
  const dismiss = useUiStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-6 left-1/2 z-[100] flex -translate-x-1/2 flex-col items-stretch gap-2"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={cn(
            'pointer-events-auto flex max-w-[420px] items-start gap-2 rounded-md border px-3 py-2 shadow-floating',
            KIND_STYLE[t.kind],
          )}
        >
          <KindIcon kind={t.kind} />
          <div className="min-w-0 flex-1 text-[12.5px] font-medium leading-snug">
            <div>{t.message}</div>
            {t.detail && (
              <div className="mt-0.5 text-[11.5px] font-normal opacity-90">
                {t.detail}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            className="ml-1 grid h-5 w-5 shrink-0 place-items-center rounded text-white/80 transition hover:bg-white/15 hover:text-white"
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
}
